import {
  AlertDialog,
  Button,
  Label,
  Spinner,
  TextArea,
  TextField,
} from "@heroui/react";
import { useState } from "react";

import { useTranslations } from "@/src/i18n/i18n";

type ModerationActionProps = {
  /** Le libellé du bouton, au repos. */
  label: string;
  /** Le libellé pendant que la requête est en vol. */
  pendingLabel: string;
  confirmTitle: string;
  /** La phrase qui **nomme** ce qui va se produire. */
  confirmBody: string;
  /** Le libellé du bouton de confirmation — jamais « OK ». */
  confirmLabel: string;
  /**
   * `danger` pour ce qui détruit ou ferme, `default` pour ce qui se défait.
   *
   * Le rouge **plein** est réservé au bouton de confirmation : c'est là, et
   * seulement là, que le clic engage.
   */
  tone?: "danger" | "default";
  isPending: boolean;
  isDisabled?: boolean;
  /** Propose un champ de motif, à l'usage de la modération seule. */
  reasonLabel?: string;
  reasonHint?: string;
  onConfirm: (_reason?: string) => void;
};

/**
 * Un geste de modération : un bouton, une confirmation, parfois un motif.
 *
 * ## Pourquoi toutes les actions passent par ici
 *
 * Elles se ressemblent au point que six copies auraient dérivé — l'une aurait
 * perdu son `role="alertdialog"`, l'autre son état d'attente. Surtout, elles
 * partagent deux règles qui ne doivent pas se négocier écran par écran :
 *
 * 1. **une action de modération se confirme**, y compris quand elle se défait :
 *    désactiver un compte coupe ses sessions, réactiver ne les rend pas ;
 * 2. **la confirmation nomme sa cible.** « Supprimer le compte 55 » n'est
 *    vérifiable par personne ; « supprimer nolan.dice » l'est. C'est l'appelant
 *    qui compose la phrase, avec le nom qu'il est seul à connaître.
 *
 * ## Le motif est facultatif, et le reste
 *
 * Le serveur le borne à 500 caractères et refuse la chaîne vide ; un champ
 * laissé blanc n'est donc **pas** envoyé — c'est le hook appelant qui l'élague.
 * Il est écrit pour l'administrateur suivant : l'intéressé ne le voit jamais,
 * et l'aide sous le champ le dit, sans quoi on l'écrirait comme un message.
 */
export function ModerationAction({
  label,
  pendingLabel,
  confirmTitle,
  confirmBody,
  confirmLabel,
  tone = "default",
  isPending,
  isDisabled = false,
  reasonLabel,
  reasonHint,
  onConfirm,
}: ModerationActionProps) {
  const t = useTranslations("moderation");
  const [isOpen, setOpen] = useState(false);
  const [reason, setReason] = useState("");

  return (
    <AlertDialog
      isOpen={isOpen}
      onOpenChange={(open) => {
        setOpen(open);
        // Le motif ne survit pas à la fermeture : rouvrir la boîte après avoir
        // renoncé ne doit pas rejouer une phrase qu'on avait justement décidé
        // de ne pas envoyer.
        if (!open) setReason("");
      }}
    >
      <Button
        className={tone === "danger" ? "text-danger" : undefined}
        isDisabled={isDisabled || isPending}
        size="sm"
        variant="ghost"
      >
        {isPending ? <Spinner color="current" size="sm" /> : null}
        {isPending ? pendingLabel : label}
      </Button>

      <AlertDialog.Backdrop>
        <AlertDialog.Container>
          {/* `role` explicite : la primitive de HeroUI rend un `dialog`
              ordinaire. Une confirmation interrompt et exige une réponse —
              c'est la définition d'un `alertdialog`, que les lecteurs d'écran
              annoncent autrement. */}
          <AlertDialog.Dialog role="alertdialog">
            <AlertDialog.Header>
              <AlertDialog.Heading>{confirmTitle}</AlertDialog.Heading>
            </AlertDialog.Header>

            <AlertDialog.Body className="flex flex-col gap-4">
              <p className="text-pretty text-muted">{confirmBody}</p>

              {reasonLabel ? (
                <TextField
                  name="reason"
                  value={reason}
                  onChange={(value) => setReason(value)}
                >
                  <Label>{reasonLabel}</Label>
                  <TextArea fullWidth rows={3} />
                  {reasonHint ? (
                    <p className="text-xs text-pretty text-muted">
                      {reasonHint}
                    </p>
                  ) : null}
                </TextField>
              ) : null}
            </AlertDialog.Body>

            <AlertDialog.Footer className="gap-2">
              <Button size="sm" variant="ghost" onPress={() => setOpen(false)}>
                {t("cancel")}
              </Button>

              <Button
                size="sm"
                variant={tone === "danger" ? "danger" : "primary"}
                onPress={() => {
                  setOpen(false);
                  onConfirm(reasonLabel ? reason : undefined);
                  setReason("");
                }}
              >
                {confirmLabel}
              </Button>
            </AlertDialog.Footer>
          </AlertDialog.Dialog>
        </AlertDialog.Container>
      </AlertDialog.Backdrop>
    </AlertDialog>
  );
}
